/**
 * report-dialog.js
 */

import React from 'react';
import axios from 'axios';
import { 
  useMediaQuery, 
  Tooltip, 
  Dialog as MuiDialog, 
  Button as MuiButton, 
  Link,
  ToggleButtonGroup,
  Alert,
  Box,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { 
  startOfMonth, 
  endOfMonth, 
  startOfYear, 
  endOfYear,
  addDays,
  addMonths,
  addYears,
  startOfDay,
  endOfDay,
  formatISO,
  parseISO
} from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { Types } from 'utils/constants';
import { objectToUrlParams, getMultiSelectHandlerOptions } from 'utils/misc';
import { UserContext } from 'contexts/user/user-context';
import { 
  DialogTitle, 
  DialogTitleDropMenu,
  DialogContent, 
  DialogActionsEnhanced 
} from 'components/dialog/dialog';
import ConfirmDialog from 'components/confirm-dialog';
import Button from 'components/button';
import Spinner from 'components/spinner';
import Icon from 'components/icon';
import MultiSelect from 'components/multi-select';
import { RecordsContext } from '../../records-context';
import ReportFrame from './report-frame';
import {
  detailReportLimit,
  reportViewCutoffMinutes,
  DefaultDateRange,
  ReportSourceType,
  ReportRunType,
  RecordSetButton,
  RecordSetType,
  DateRange,
  ReportType,
  ReportTypeInfo,
  reportAnalytics,
} from './report-constants';
import {
  SectionTitle,
  RecordSelector,
  GridContainer,
  GridItem,
  GridItemDefined,
  Select,
  ReportTypeSelect,
  InfoText,
  StatusInfo
} from './report-components';
import InlineHelpLink from 'components/inline-help-link';


const useStyles = makeStyles(theme => ({
  root: {
    '& .MuiDialog-paper': {
      transition: '300ms ease-out'
    },
    '&.viewReport .MuiDialog-paper': {
      height: '100%'
    },
    '&.viewReport .MuiDialogContentText-root': {
      height: '100%',
      marginBottom: 0,
    }
  },
  titleBar: {
    '& .MuiTypography-root': {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    [theme.breakpoints.down('sm')]: {
      padding:`${theme.spacing(1.5)} ${theme.spacing(2)}` 
    }
  },
  content: {
    [theme.breakpoints.down('sm')]: {
      padding: `${theme.spacing(1)} ${theme.spacing(1.5)}` 
    }
  },
  mobileReportContent: {
    [theme.breakpoints.down('sm')]: {
      padding: 0
    }
  },
  sectionContent: {
    padding: `0 ${theme.spacing(1)} ${theme.spacing(2)} ${theme.spacing(5)}`,
    [theme.breakpoints.up(480)]: {
      padding: `0 ${theme.spacing(6)} ${theme.spacing(2)} ${theme.spacing(6)}`,
    },
  },
  recordToggleGroup: {
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
      marginLeft: theme.spacing(-1),
      marginRight: theme.spacing(-1),
    }
  },
  actions: {
    justifyContent: 'space-between',
    '& .right': {
      display: 'flex',
    },
    [theme.breakpoints.down(720)]: {
      flexDirection: 'column',
      '& .right': {
        marginTop: theme.spacing(2),
        marginLeft: 0,
      }
    }    
  },
  reportCount: {
    display: 'flex',
    alignItems: 'center',
    minHeight: 40,
    maxWidth: 490,
    marginLeft: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      marginLeft: 0
    },
    '& .pt-spinner': {
      height: '100%',
      width: 'auto'
    },
    '& .MuiSvgIcon-root': {
      marginRight: theme.spacing(2),
    },
    '& .MuiTypography-root': {
      color: theme.palette.sys.onSurface,
      fontWeight: 400,
      lineHeight: 1.2,
      '& .number': {
        color: theme.palette.sys.primary,
        fontSize: theme.typography.sizes[4],
        fontWeight: 'bold',
      },
    },
  },
}));


export default function ReportDialog(props) {
  const {
    open,
    filterParams,
    isFilterActive,
    handlerDogs,
    supervisors,
    managedHandlers,
    recordCount,
    filteredRecordCount,
    selectedRecordCount,
    searchedRecordType,
    selectedRecords,
  } = props;
  const classes = useStyles();
  const { user } = React.useContext(UserContext);
  const { setIsFilterOpen } = React.useContext(RecordsContext);
  const { t } = useTranslation('general');
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'), { noSsr: true });
  const { enqueueSnackbar } = useSnackbar();
  const [recordGroups, setRecordGroups] = React.useState([]);
  const [recordSetButton, setRecordSetButton] = React.useState(RecordSetButton.STANDARD);
  const [recordSetType, setRecordSetType] = React.useState();
  const [reportType, setReportType] = React.useState();
  const [dog, setDog] = React.useState('');
  const [dogOptions, setDogOptions] = React.useState([]);
  const [handlers, setHandlers] = React.useState([]);
  const [handlerOptions, setHandlerOptions] = React.useState([]);
  const [dateRange, setDateRange] = React.useState(DefaultDateRange);
  const [results, setResults] = React.useState();
  const [runReport, setRunReport] = React.useState(false);
  const [confirmDownloadReport, setConfirmDownloadReport] = React.useState();
  const hasSearchedDog = (recordSetType === RecordSetType.SEARCHED && Boolean(filterParams.dogId));
  const hasSearchedHandlers = (recordSetType === RecordSetType.SEARCHED && Boolean(filterParams.handlers));
  const hasSearchedDate = (recordSetType === RecordSetType.SEARCHED && Boolean(filterParams.date || filterParams.startDate));
  const isOverDetailedLimit = React.useMemo(() => {
    return (
      [ReportType.EXERCISE_DETAIL, ReportType.DEPLOYMENT_DETAIL, ReportType.CLASS_DETAIL, ReportType.VET_VISIT_DETAIL].includes(reportType)
      && results?.totalCount > detailReportLimit
    );
  }, [reportType, results]);
  
  // selected records stuff
  const selectedRecordTypes = React.useMemo(() => {
    const types = [];
    if (selectedRecords[Types.RecordGroup.TRAINING].length > 0) types.push(Types.RecordGroup.TRAINING);
    if (selectedRecords[Types.RecordGroup.DEPLOYMENT].length > 0) types.push(Types.RecordGroup.DEPLOYMENT);
    if (selectedRecords[Types.RecordGroup.CLASS].length > 0) types.push(Types.RecordGroup.CLASS);
    if (selectedRecords[Types.RecordGroup.VET_VISIT].length > 0) types.push(Types.RecordGroup.VET_VISIT);
    return types;
  }, [selectedRecords]);
  const selectedRecordsParams = React.useMemo(() => {
    if (recordSetType === RecordSetType.SELECTED) {
      return {
        exerciseIds: selectedRecords[Types.RecordGroup.TRAINING].length > 0
          ? selectedRecords[Types.RecordGroup.TRAINING]
          : [],
        deploymentIds: selectedRecords[Types.RecordGroup.DEPLOYMENT].length > 0
          ? selectedRecords[Types.RecordGroup.DEPLOYMENT]
          : [],
        classIds: selectedRecords[Types.RecordGroup.CLASS].length > 0
          ? selectedRecords[Types.RecordGroup.CLASS]
          : [],
        vetVisitIds: selectedRecords[Types.RecordGroup.VET_VISIT].length > 0
          ? selectedRecords[Types.RecordGroup.VET_VISIT]
          : []
      };
    } else {
      return {};
    }
  }, [recordSetType, selectedRecords]);

  // duration estimation
  const durationEstimation = React.useMemo(() => {
    const waitTime = (minutes) => {
      const minutesCeil = Math.ceil(minutes);
      return minutesCeil <= 1
        ? 'about a minute'
        : `${minutesCeil} - ${minutesCeil * 2} minutes`;
    }

    if (!reportType || !results) {
      return {
        seconds: 0,
        minutes: 0,
        waitTime: 'none',
        exportMinutes: 0,
        exportWaitTime: 'none'
      };
    } else {
      const reportEstimateMinutes = results.estimatedDurationSeconds / 60;
      const totalEstimateMinutes = reportEstimateMinutes + (results.exportQueueWaitTimeSeconds / 60);
      return {
        seconds: results.estimatedDurationSeconds,
        minutes: reportEstimateMinutes,
        waitTime: waitTime(reportEstimateMinutes),
        exportMinutes: totalEstimateMinutes,
        exportWaitTime: waitTime(totalEstimateMinutes)
      };
    }
  }, [reportType, results]);


  // -------------------------------------------------------------------------------
  // on change report type
  // -------------------------------------------------------------------------------
  const onReportTypeChange = React.useCallback((newReportType) => {

    // determines record type from the report type
    const getRecordGroups = (reportType) => {
      // TRAINING
      if ([
        ReportType.TRAINING_SUMMARY
      ].includes(reportType)) {
        return [Types.RecordGroup.TRAINING, Types.RecordGroup.CLASS];
      }
      // DOG TRAINING (EXERCISE)
      else if ([
        ReportType.EXERCISE_DETAIL,
        ReportType.EXERCISE_LOG,
        ReportType.EXERCISE_ODORS
      ].includes(reportType)) {
        return [Types.RecordGroup.TRAINING];
      } 
      // DEPLOYMENT
      else if ([
        ReportType.DEPLOYMENT_DETAIL,
        ReportType.DEPLOYMENT_SUMMARY,
        ReportType.DEPLOYMENT_LOG
      ].includes(reportType)) {
        return [Types.RecordGroup.DEPLOYMENT];
      } 
      // CLASS
      else if ([
        ReportType.CLASS_DETAIL
      ].includes(reportType)) {
        return [Types.RecordGroup.CLASS];
      }
      // VET VISIT
      else if ([
        ReportType.VACCINATION_SUMMARY,
        ReportType.VET_VISIT_DETAIL
      ].includes(reportType)) {
        return [Types.RecordGroup.VET_VISIT];
      }
    }

    if (newReportType === ReportType.VACCINATION_SUMMARY) {
      onDateRangeSelectionChange(DateRange.ALL);
    }

    setReportType(newReportType);
    setRecordGroups(getRecordGroups(newReportType));
  }, []);


  // -------------------------------------------------------------------------------
  // on record group type change
  // -------------------------------------------------------------------------------
  const onRecordSetChange = React.useCallback((val) => {
    if (!val) {
      return;
    }

    setRecordSetButton(val);
    const newRecordSetType = val === RecordSetButton.STANDARD
      ? RecordSetType.ALL
      : selectedRecords?.count > 0
        ? RecordSetType.SELECTED
        : RecordSetType.SEARCHED;
    setRecordSetType(newRecordSetType);

    // set handlers to searched handlers if 'searched' group type
    if (newRecordSetType === RecordSetType.SEARCHED) {
      setDog(p => filterParams.dogId || p);
      setHandlers(filterParams.handlers || []);
      if (filterParams.date) {
        setDateRange({
          id: DateRange.CUSTOM_RANGE,
          start: parseISO(filterParams.date),
          end: parseISO(filterParams.date)
        });
      } else if (filterParams.startDate) {
        setDateRange({ 
          id: DateRange.CUSTOM_RANGE, 
          start: parseISO(filterParams.startDate), 
          end: parseISO(filterParams.endDate)
        });
      } else {
        setDateRange(DefaultDateRange);
      }
    } else {

      setHandlers([]);
      setDateRange(DefaultDateRange);
    }

    // set date range to all if 'selected' group type
    if (newRecordSetType === RecordSetType.SELECTED) {
      setDateRange(DefaultDateRange);
    }
  }, [selectedRecords, filterParams]);


  // -------------------------------------------------------------------------------
  // init/reset
  // -------------------------------------------------------------------------------
  React.useEffect(() => {
    if (open) {
      const initSetButton = selectedRecordCount > 0 || filteredRecordCount > 0
        ? RecordSetButton.CUSTOM
        : RecordSetButton.STANDARD;
      onRecordSetChange(initSetButton);

      // default report type
      if (selectedRecordCount > 0) {
        if (selectedRecordTypes.includes(Types.RecordGroup.TRAINING)) {
          onReportTypeChange(ReportType.EXERCISE_DETAIL);
        } else if (selectedRecordTypes.includes(Types.RecordGroup.DEPLOYMENT)) {
          onReportTypeChange(ReportType.DEPLOYMENT_DETAIL);
        } else if (selectedRecordTypes.includes(Types.RecordGroup.CLASS)) {
          onReportTypeChange(ReportType.CLASS_DETAIL);
        } else if (selectedRecordTypes.includes(Types.RecordGroup.VET_VISIT)) {
          onReportTypeChange(ReportType.VET_VISIT_DETAIL);
        }
      }
    } else {
      setRecordGroups([]);
      setDateRange(DefaultDateRange);
      setRunReport(false);
    }
  }, [open, onRecordSetChange, onReportTypeChange, filteredRecordCount, selectedRecordCount, selectedRecordTypes]);


  // -------------------------------------------------------------------------------
  // update dog / handler options
  // -------------------------------------------------------------------------------
  React.useEffect(() => {
    if (user) {
      if (user.role.isHandler && handlerDogs) {
        const dogOptions = handlerDogs[0].dogs.map(d => ({
          value: d.id,
          label: d.name
        }));
        setDogOptions(dogOptions);
        setDog(dogOptions[0]?.value);
      } else if (!user.role.isHandler && (supervisors || managedHandlers)) {
        setHandlerOptions(getMultiSelectHandlerOptions(user, supervisors, managedHandlers));
      }
    }
  }, [user, handlerDogs, supervisors, managedHandlers]);


  // -------------------------------------------------------------------------------
  // query the result records
  // -------------------------------------------------------------------------------
  React.useEffect(() => {
    (async () => {
      if (open && recordGroups.length > 0) {
        setResults();

        let params = { reportType, recordGroups };

        if (recordSetType === RecordSetType.SEARCHED) {
          params = {
            ...params,
            ...filterParams,
          };
        } else if (recordSetType === RecordSetType.SELECTED) {
          params = {
            ...params,
            ...selectedRecordsParams
          };
        }

        if (dog && dog !== 'all') {
          params.dogId = dog;
        }
        
        if (handlers && handlers.length > 0) {
          params.handlers = handlers;
        }

        if (dateRange.id !== DateRange.ALL) {
          params.startDate = !isNaN(dateRange.start) ? formatISO(startOfDay(dateRange.start)) : '';
          params.endDate = !isNaN(dateRange.end) ? formatISO(endOfDay(dateRange.end)) : '';
        }

        const response = await axios.get(`/records/report-search`, { params: objectToUrlParams(params) });
        if (response.status === 200) {
          setResults(response.data);
        }
      }
    })();
  }, [
    open, 
    reportType,
    recordGroups, 
    filterParams, 
    recordSetType, 
    dog, 
    handlers, 
    dateRange.id,
    dateRange.start,
    dateRange.end, 
    selectedRecords,
    selectedRecordsParams
  ]);


  // -------------------------------------------------------------------------------
  // on click search link
  // -------------------------------------------------------------------------------
  const onClickSearchLink = (e) => {
    e.stopPropagation();
    onClose();
    setIsFilterOpen(true);

    // scroll to records-searchbar
    const element = document.querySelector(`#records-searchbar`);
    if (element) {
      setTimeout(() => element.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  }


  // -------------------------------------------------------------------------------
  // on click records link
  // -------------------------------------------------------------------------------
  const onClickRecordsLink = (e) => {
    e.stopPropagation();
    onClose();

    // scroll to records-list
    const element = document.querySelector(`#records-list`);
    if (element) {
      setTimeout(() => element.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  }


  // -------------------------------------------------------------------------------
  // on date range dropdown change
  // -------------------------------------------------------------------------------
  const onDateRangeSelectionChange = (val) => {
    switch (val) {
      case DateRange.ALL:
        setDateRange({
          id: val,
          start: null,
          end: null
        });
        break;

      case DateRange.THIS_MONTH:
        setDateRange({
          id: val,
          start: startOfMonth(new Date()),
          end: endOfMonth(new Date())
        });
        break;

      case DateRange.LAST_MONTH:
        setDateRange({
          id: val,
          start: startOfMonth(addMonths(new Date(), -1)),
          end: endOfMonth(addMonths(new Date(), -1))
        });
        break;

      case DateRange.LAST_30:
        setDateRange({
          id: val,
          start: addDays(new Date(), -30),
          end: new Date()
        });
        break;

      case DateRange.LAST_90:
        setDateRange({
          id: val,
          start: addDays(new Date(), -90),
          end: new Date()
        })
        break;

      case DateRange.THIS_YEAR:
        setDateRange({
          id: val,
          start: startOfYear(new Date()),
          end: endOfYear(new Date())
        });
        break;

      case DateRange.LAST_YEAR:
        setDateRange({
          id: val,
          start: startOfYear(addYears(new Date(), -1)),
          end: endOfYear(addYears(new Date(), -1))
        });
        break;

      case DateRange.CUSTOM_RANGE:
        // default to last 30 days
        setDateRange({
          id: val,
          start: addDays(new Date(), -30),
          end: new Date()
        });
        break;

      default:
        break;
    }
  }


  // -------------------------------------------------------------------------------
  // hackily shows intercom article in a way that always works
  // -------------------------------------------------------------------------------
  const showIntercomArticle = (articleId) => {
    // window.Intercom('shutdown');
    // window.Intercom("update", {
    //   app_id: "o89sg5k0",
    //   email: user.email,
    // });
    window.Intercom('showArticle', articleId);
  }


  // -------------------------------------------------------------------------------
  // on close
  // -------------------------------------------------------------------------------
  const onClose = () => {
    setRecordSetType();
    setReportType();
    setRecordGroups([]);
    props.onClose();
  }


  // -------------------------------------------------------------------------------
  // on view
  // -------------------------------------------------------------------------------
  const onViewReport = () => {
    reportAnalytics(ReportSourceType.MULTIPLE, ReportRunType.VIEW, recordSetType, reportType, results.totalCount, t);
    setRunReport(true);
  }


  // -------------------------------------------------------------------------------
  // on download
  // -------------------------------------------------------------------------------
  const onExportReport = async () => {
    if (await confirmDownloadReport()) {
      reportAnalytics(ReportSourceType.MULTIPLE, ReportRunType.DOWNLOAD, recordSetType, reportType, results.totalCount, t);
      try {
        const response = await axios.post(`/Report/GeneratePT2Report`, {
          export: true,
          roleId: user.role.id,
          reportType,
          localDateTime: formatISO(new Date()),
          recordGroups,
          ...(recordSetType === RecordSetType.SEARCHED ? filterParams : {}),
          startDate: dateRange.start ? formatISO(startOfDay(dateRange.start)) : '',
          endDate: dateRange.end ? formatISO(endOfDay(dateRange.end)) : '',
          dogId: dog !== 'all' ? dog : '',
          handlers,
          ...selectedRecordsParams
        }, {
          baseURL: process.env.REACT_APP_HOST
        });
        if (response.isSuccess) {
          enqueueSnackbar(`Export queued`, {
            variant: 'success'
          });
        }
      } catch (e) {
        if (e?.response?.status === 409) {
          enqueueSnackbar(`You already have an export in progress for this report.`, {
            variant: 'error',
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'center'
            }
          });
        } else {
          enqueueSnackbar(`There was an error exporting your report. Please contact support if this error persists.`, {
            variant: 'error',
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'center'
            }
          });
        }
      }
    }
  }


  const enabledReportGroups = React.useMemo(() => {
    return recordSetType === undefined
      ? undefined
      : recordSetType === RecordSetType.SELECTED
        ? selectedRecordTypes
        : recordSetType === RecordSetType.SEARCHED && searchedRecordType
          ? [searchedRecordType]
          : null;
      
    }, [selectedRecordTypes, recordSetType, searchedRecordType]);

  const disabledReportTypes = React.useMemo(() => {
    return recordSetType === undefined
      ? undefined
      : recordSetType === RecordSetType.SELECTED || recordSetType === RecordSetType.SEARCHED
        ? [ReportType.VACCINATION_SUMMARY]
        : null;
    }, [recordSetType]);


  return <>
    <MuiDialog
      className={`${classes.root} ${runReport ? 'viewReport' : ''}`}
      data-testid='report-dialog'
      maxWidth='md'
      fullWidth={true}
      fullScreen={isMobile}
      disableEnforceFocus={true}
      open={open}
      onClose={onClose}
    >
      <DialogTitle 
        className={classes.titleBar}
        icon='report'
        title='Create Report'
        actions={
          <DialogTitleDropMenu
            id={`report-dialog-help-menu`}
            aria-label="report dialog help menu"
            onSelect={item => item.action()}
            label={(isOpen, onClick, arrowIcon) => (
              <MuiButton
                startIcon={<Icon type='help' />}
                endIcon={arrowIcon}
                children={'Help Topics'}
                onClick={onClick}
              />
            )}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            items={[{
              key: 'work',
              name: 'How does reporting work? (general overview - start here)',
              onSelect: () => showIntercomArticle(6716555)
            }, {
              key: 'types',
              name: 'What types of reports can I create? (includes samples)',
              onSelect: () => showIntercomArticle(6685221)
            }, {
              key: 'single',
              name: 'How do I create a report for just one record?',
              onSelect: () => showIntercomArticle(6684704)
            }, {
              key: 'multiple',
              name: 'How do I create a report that includes multiple records?',
              onSelect: () => showIntercomArticle(6716568)
            }, {
              key: 'custom',
              name: 'How do I create a customized report that includes only the records I specify?',
              onSelect: () => showIntercomArticle(6716580),
            }, {
              key: 'court01',
              name: `How do I create a report for court with my dog's entire exercise history?`,
              onSelect: () => showIntercomArticle(6716570)
            },
            ]}
          />
        }
      />

      <DialogContent 
        className={`flex-fh ${classes.content} ${runReport ? classes.mobileReportContent : ''}`}
        contentTextClassName='flex-fh'
      >
        <Box sx={{ display: runReport ? 'none' : 'block' }}>
          {/* SECTION 1 */}
          <SectionTitle
            number={1}
            text={<>
              Choose which report <b>mode</b> to use
              <InlineHelpLink onClick={(e) => showIntercomArticle(6716568)} />
            </>}
          />
          <div className={classes.sectionContent}>
            <ToggleButtonGroup 
              exclusive 
              className={classes.recordToggleGroup}
              value={recordSetButton}
              onChange={(e, v) => onRecordSetChange(v)}
            >
              <RecordSelector
                value={RecordSetButton.STANDARD}
                iconName='report-all'
                title='Standard Report'
                subtitle={`ALL ${recordCount} RECORD${recordCount === 1 ? '' : 'S'}`}
                text={`Use all of my records. I'll select the report type and any filters below.`}
              />
              <RecordSelector
                value={RecordSetButton.CUSTOM}
                disabled={!isFilterActive && selectedRecordCount === 0}
                iconName='report-searched'
                title='Custom Report'
                subtitle={selectedRecordCount > 0
                  ? `${selectedRecordCount} SELECTED RECORD${selectedRecordCount === 1 ? '' : 'S'}`
                  : `${filteredRecordCount} SEARCHED RECORD${filteredRecordCount === 1 ? '' : 'S'}`}
                text={selectedRecordCount > 0
                  ? `I already checkmarked the specific records that I want to use for this report.`
                  : isFilterActive
                    ? `I already ran a search to show the specific records that I want to use for this report.`
                    : <>
                      To enable this option: first run a<> </>
                      <Link
                        children='SEARCH'
                        style={{ pointerEvents: 'all', cursor: 'pointer' }}
                        onClick={onClickSearchLink}
                        underline="hover" /> 
                      {!isMobile 
                        ? (<> or<> </>
                        <Link
                          children='CHECKMARK'
                          style={{ pointerEvents: 'all', cursor: 'pointer' }}
                          onClick={onClickRecordsLink}
                          underline="hover" /> the records that you want to use</>
                        )
                        : ''} 
                      <> </>on the Records page.
                    </>
                }
              />
            </ToggleButtonGroup>
          </div>

          {/* SECTION 2 */}
          <SectionTitle
            number={2}
            text={<>Select the <b>type</b> of report to run 
              <InlineHelpLink onClick={() => showIntercomArticle(6685221)} />
            </>}
          />
          <div className={classes.sectionContent}>
            <GridContainer>
              <GridItemDefined>
                {enabledReportGroups !== undefined && disabledReportTypes !== undefined &&
                  <ReportTypeSelect
                    value={reportType}
                    onChange={onReportTypeChange}
                    enabledReportGroups={enabledReportGroups}
                    disabledReportTypes={disabledReportTypes}
                  />
                }
              </GridItemDefined>
              <GridItem xs={12} sm={6} md={4}>
                <InfoText>
                  {ReportTypeInfo[reportType]}
                </InfoText>
              </GridItem>
            </GridContainer>
          </div>

          {/* SECTION 3 */}
          <SectionTitle
            number={3}
            text={<>OPTIONAL: Provide any additional <b>filtering</b> to apply to these records</>}
          />
          <div className={classes.sectionContent}>
            <GridContainer>
              {user.role.isHandler
                ? <>
                  <GridItemDefined>
                    <Tooltip
                      arrow
                      placement='top'
                      title={reportType === ReportType.CLASS_DETAIL
                        ? <>Dogs are not associated with training classes.</>
                        : ''}
                    >
                      <span style={{ display: 'flex', flex: 1 }}>
                        <Select
                          label='Dog'
                          value={dog}
                          onChange={(ev, el) => setDog(el.props.value)}
                          options={dogOptions}
                          disabled={reportType === ReportType.CLASS_DETAIL || hasSearchedDog}
                        />
                      </span>
                    </Tooltip>
                  </GridItemDefined>
                  {hasSearchedDog &&
                  <GridItem xs={12} sm={6} md={4}>
                    <InfoText isWarning>
                      A dog has already been specified by your record list search.
                    </InfoText>
                  </GridItem>}
                </>
                : <>
                  <GridItemDefined>
                    <MultiSelect
                      id='print-report-handler-select'
                      options={handlerOptions}
                      onChange={setHandlers}
                      value={handlers}
                      disabled={hasSearchedHandlers}
                      label='Handlers'
                      nounPlural='Handlers'
                    />
                  </GridItemDefined>
                  {(hasSearchedHandlers) &&
                  <GridItem xs={12} sm={6} md={4}>
                    <InfoText isWarning>
                      {hasSearchedHandlers
                        ? 'Handlers have already been specified by your record list search.'
                        : ''}
                    </InfoText>
                  </GridItem>}
                </>
              }
            </GridContainer>

            <GridContainer>
              <GridItemDefined>
                <Tooltip
                  arrow
                  placement='top'
                  title={recordSetType === RecordSetType.SELECTED
                    ? <>Date ranges cannot be customized for Selected records.</>
                    : ''}
                >
                  <span style={{ display: 'flex', flex: 1 }}>
                    <Select
                      label='Date Range'
                      value={dateRange.id}
                      onChange={(ev, el) => onDateRangeSelectionChange(el.props.value)}
                      disabled={recordSetType === RecordSetType.SELECTED || hasSearchedDate || reportType === ReportType.VACCINATION_SUMMARY}
                      options={[
                        { value: DateRange.ALL, label: 'All' },
                        { value: DateRange.THIS_MONTH, label: 'This Month' },
                        { value: DateRange.LAST_MONTH, label: 'Last Month' },
                        { value: DateRange.LAST_30, label: 'Last 30 Days' },
                        { value: DateRange.LAST_90, label: 'Last 90 Days' },
                        { value: DateRange.THIS_YEAR, label: 'This Year' },
                        { value: DateRange.LAST_YEAR, label: 'Last Year' },
                        { value: DateRange.CUSTOM_RANGE, label: 'Custom Range' },
                      ]}
                    />
                  </span>
                </Tooltip>
              </GridItemDefined>

              <GridItemDefined className='date-range-spacer' />

              {dateRange.id !== DateRange.ALL && recordSetType !== RecordSetType.SELECTED &&
              <GridItemDefined>
                <DatePicker
                  closeOnSelect
                  slotProps={{
                    textField: { variant: 'outlined', size: 'small' },
                  }}
                  label='Start'
                  format="MM/dd/yyyy"
                  value={dateRange.start}
                  onChange={date => setDateRange(p => ({ ...p, id: DateRange.CUSTOM_RANGE, start: date }))}
                  disabled={hasSearchedDate || reportType === ReportType.VACCINATION_SUMMARY}
                />
              </GridItemDefined>}

              {dateRange.id !== DateRange.ALL && recordSetType !== RecordSetType.SELECTED &&  
              <GridItemDefined>
                <DatePicker
                  closeOnSelect
                  slotProps={{
                    textField: { variant: 'outlined', size: 'small' },
                  }}
                  label='End'
                  format="MM/dd/yyyy"
                  value={dateRange.end}
                  onChange={date => setDateRange(p => ({ ...p, id: DateRange.CUSTOM_RANGE, end: date }))}
                  disabled={hasSearchedDate || reportType === ReportType.VACCINATION_SUMMARY}
                />
              </GridItemDefined>}

              {hasSearchedDate &&
              <GridItem xs={12} md={3}>
                <InfoText isWarning>
                  A date has already been specified by your record list search.
                </InfoText>  
              </GridItem>}
            </GridContainer>
          </div>
        </Box>

        {/* REPORT FRAME */}
        <ReportFrame
          doRun={runReport}
          reportSourceType={ReportSourceType.MULTIPLE}
          recordSetType={recordSetType}
          params={{
            reportType,
            recordGroups,
            ...(recordSetType === RecordSetType.SEARCHED ? filterParams : {}),
            startDate: dateRange.start && !isNaN(dateRange.start) ? formatISO(startOfDay(dateRange.start)) : '',
            endDate: dateRange.end && !isNaN(dateRange.end) ? formatISO(endOfDay(dateRange.end)) : '',
            dogId: dog !== 'all' ? dog : '',
            handlers,
            ...selectedRecordsParams
          }}
        />
      </DialogContent>

      <DialogActionsEnhanced className={classes.actions}>
        {/* completed records count & info */}
        <div className={classes.reportCount}>
          {!results &&
          <Spinner />}
          <StatusInfo
            reportType={reportType}
            results={results}
            durationEstimation={durationEstimation}
          />
        </div>

        {/* actions */}
        <div className='right MuiDialogActions-spacing'>
          {!runReport &&
          <>
            {/* cancel */}
            <MuiButton
              children='Cancel'
              onClick={onClose}
            />
            {/* download report */}
            <Tooltip
              arrow
              placement='top'
              title={isOverDetailedLimit
                ? <>Detail reports are limited to {detailReportLimit} completed<br />records. Please adjust your filters.</>
                : ''}
            >
              <span style={{ display: 'inline-block' }}>
                <MuiButton
                  children='Download&nbsp;...'
                  startIcon={<Icon type='download' />}
                  onClick={onExportReport}
                  color='primary'
                  variant='outlined'
                  disabled={
                    !results
                    || results.totalCount === 0
                    || isOverDetailedLimit
                  }
                />
              </span>
            </Tooltip>
            {/* view report */}
            <Tooltip
              arrow
              placement='top'
              title={isOverDetailedLimit
                ? <>Detail reports are limited to {detailReportLimit} completed<br />records. Please adjust your filters.</>
                : durationEstimation.minutes > reportViewCutoffMinutes
                  ? <>This report will take too long to view. Choose DOWNLOAD instead.</>
                  : ''}
            >
              <span style={{ display: 'inline-block' }}>
                <Button
                  children='VIEW'
                  startIcon={<Icon type='view' />}
                  onClick={() => onViewReport()}
                  color='secondary'
                  variant='contained'
                  disabled={
                    !results
                    || results.totalCount === 0
                    || isOverDetailedLimit
                    || durationEstimation.minutes > 2
                  }
                />
              </span>
            </Tooltip>
          </>}
          {runReport &&
          <>
            {/* back */}
            <MuiButton
              children='Back'
              onClick={() => setRunReport(false)}
            />
            {/* done */}
            <MuiButton
              children='Done'
              onClick={onClose}
              color='primary'
              variant='outlined'
            />
          </>}
        </div>
      </DialogActionsEnhanced>
    </MuiDialog>

    {/* delete file confirm dialog */}
    <ConfirmDialog
      // titleStyle="warning"
      setConfirm={setConfirmDownloadReport}
      title='Download Report'
      titleIcon='download'
      content={<>
        <>
          Click GENERATE REPORT to begin. 
          You will receive a notification and an email with a download link when it is complete.
        </>
        <br /><br />
        <span className={durationEstimation.minutes > 10 ? 'text-warning bold' : ''}>
          Estimated wait time: {durationEstimation.exportWaitTime}.
        </span>
        {durationEstimation.exportMinutes > durationEstimation.minutes &&
        <Alert severity='warning' sx={{ mt: 2 }}>NOTE: The report may take longer than expected due to high system load.</Alert>}
      </>}
      customActions={[{
        label: 'Cancel',
        value: false,
        isNotPriority: true,
      },
      {
        label: 'Generate Report',
        value: true,
      }]}
    />
  </>;
}